export const AI_NOT_FOUND = 'NOT FOUND';

export type AiAppliance = {
  Appliance_Type?: string | null;
  Name?: string | null;
  Brand?: string | null;
  Model_Number?: string | null;
  Serial_Number?: string | null;
  Date_of_Manufacture?: string | null;
};

export type AssetFormAttribute = { id: string; value: string };

export type NamedAttribute = { id: string; name?: string | null };

export type AiUploadedImage = { id: string; name?: string | null };

export type AiAnalysisFormUpdates = {
  type?: string;
  name?: string;
  attributes?: AssetFormAttribute[];
};

export function isBlankField(value: unknown): boolean {
  return (
    value == null ||
    value === '' ||
    (typeof value === 'string' && value.trim() === '')
  );
}

export function findAttributeId(
  attributes: NamedAttribute[],
  searchNames: string[]
): string | null {
  const normalizedSearch = searchNames.map((n) => n.toLowerCase().trim());
  const found = attributes.find((attr) => {
    const attrName = attr.name?.toLowerCase().trim() || '';
    return normalizedSearch.some(
      (search) =>
        attrName === search ||
        attrName.includes(search) ||
        search.includes(attrName)
    );
  });
  return found?.id || null;
}

function shouldApplyAiText(
  current: unknown,
  aiValue: string | null | undefined
): string | null {
  const trimmed = aiValue?.trim();
  if (!isBlankField(current) || !trimmed || trimmed === AI_NOT_FOUND) {
    return null;
  }
  return trimmed;
}

function applyMappedAttributes(
  currentAttributes: AssetFormAttribute[],
  availableAttributes: NamedAttribute[],
  appliance: AiAppliance
): { attributes: AssetFormAttribute[]; changed: boolean } {
  const attributeMappings: Array<{ searchNames: string[]; value: string }> = [];

  if (appliance.Brand && appliance.Brand !== AI_NOT_FOUND) {
    attributeMappings.push({
      searchNames: ['brand', 'make', 'manufacturer'],
      value: appliance.Brand,
    });
  }

  if (appliance.Model_Number && appliance.Model_Number !== AI_NOT_FOUND) {
    attributeMappings.push({
      searchNames: ['model number', 'model', 'model no', 'model #'],
      value: appliance.Model_Number,
    });
  }

  if (appliance.Serial_Number && appliance.Serial_Number !== AI_NOT_FOUND) {
    attributeMappings.push({
      searchNames: ['serial number', 'serial', 'serial no', 'serial #'],
      value: appliance.Serial_Number,
    });
  }

  if (
    appliance.Date_of_Manufacture &&
    appliance.Date_of_Manufacture !== AI_NOT_FOUND
  ) {
    attributeMappings.push({
      searchNames: [
        'date of manufacture',
        'manufacture date',
        'manufactured',
        'date manufactured',
      ],
      value: appliance.Date_of_Manufacture,
    });
  }

  if (attributeMappings.length === 0) {
    return { attributes: currentAttributes, changed: false };
  }

  const updatedAttributes = [...currentAttributes];
  let attributesWereUpdated = false;

  attributeMappings.forEach(({ searchNames, value }) => {
    const attributeId = findAttributeId(availableAttributes, searchNames);
    if (!attributeId) return;

    const existingIndex = updatedAttributes.findIndex(
      (attr) => attr.id === attributeId
    );

    if (existingIndex >= 0) {
      const existingValue = updatedAttributes[existingIndex].value;
      if (!existingValue || existingValue.trim() === '') {
        updatedAttributes[existingIndex] = {
          id: attributeId,
          value,
        };
        attributesWereUpdated = true;
      }
    } else {
      updatedAttributes.push({
        id: attributeId,
        value,
      });
      attributesWereUpdated = true;
    }
  });

  return { attributes: updatedAttributes, changed: attributesWereUpdated };
}

function applyImageAttribute(
  attributes: AssetFormAttribute[],
  availableAttributes: NamedAttribute[],
  uploadedImage: AiUploadedImage
): AssetFormAttribute[] {
  const imageAttributeId = findAttributeId(availableAttributes, [
    'photo',
    'image',
    'picture',
    'reference image',
    'reference photo',
  ]);
  if (!imageAttributeId) {
    return attributes;
  }

  const updatedAttributes = [...attributes];
  const existingImageAttrIndex = updatedAttributes.findIndex(
    (attr) => attr.id === imageAttributeId
  );

  const imageForAttribute = {
    id: uploadedImage.id,
    kind: 'image',
    originalFilename: uploadedImage.name || 'image.jpg',
  };

  if (existingImageAttrIndex >= 0) {
    const existingValue = updatedAttributes[existingImageAttrIndex].value;
    let currentImages: Array<{
      id: string;
      kind: string;
      originalFilename: string;
    }> = [];
    if (existingValue) {
      try {
        currentImages = JSON.parse(existingValue);
      } catch {
        currentImages = [];
      }
    }

    if (!currentImages.some((img) => img.id === uploadedImage.id)) {
      currentImages.push(imageForAttribute);
      updatedAttributes[existingImageAttrIndex] = {
        id: imageAttributeId,
        value: JSON.stringify(currentImages),
      };
    }
  } else {
    updatedAttributes.push({
      id: imageAttributeId,
      value: JSON.stringify([imageForAttribute]),
    });
  }

  return updatedAttributes;
}

export function applyAiAnalysisToForm({
  currentType,
  currentName,
  currentAttributes,
  liveAttributes,
  appliance,
  availableAttributes,
  uploadedImage,
}: {
  currentType: unknown;
  currentName: unknown;
  currentAttributes: AssetFormAttribute[];
  liveAttributes?: AssetFormAttribute[];
  appliance: AiAppliance;
  availableAttributes: NamedAttribute[];
  uploadedImage?: AiUploadedImage | null;
}): AiAnalysisFormUpdates {
  const updates: AiAnalysisFormUpdates = {};

  const nextType = shouldApplyAiText(currentType, appliance.Appliance_Type);
  if (nextType) {
    updates.type = nextType;
  }

  const nextName = shouldApplyAiText(currentName, appliance.Name);
  if (nextName) {
    updates.name = nextName;
  }

  const mapped = applyMappedAttributes(
    currentAttributes,
    availableAttributes,
    appliance
  );
  if (mapped.changed) {
    updates.attributes = mapped.attributes;
  }

  if (uploadedImage) {
    const imageAttributeId = findAttributeId(availableAttributes, [
      'photo',
      'image',
      'picture',
      'reference image',
      'reference photo',
    ]);
    if (imageAttributeId) {
      const attributesForImage =
        updates.attributes || liveAttributes || currentAttributes;
      updates.attributes = applyImageAttribute(
        attributesForImage,
        availableAttributes,
        uploadedImage
      );
    }
  }

  return updates;
}
